// src/modules/ServiceProviderSelectResponse.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Stack,
  Box,
} from "@mui/material";
import axios from "axios";

const TAG_NOTIFY = "notify_audit_was_done";

const ServiceProviderSelectResponse = ({
  module,
  value, // could be legacy array OR new { selection: [...], tags: [...] }
  isEditable,
  onChange,
  companyId,
  requestedTo,
}) => {
  const [providers, setProviders] = useState([]);
  const API_BASE_URL = "https://formbuilderapi.getforsite.com";

  // decide which company to query
  const activeCompanyId = companyId || requestedTo;

  useEffect(() => {
    const fetchProviders = async () => {
      if (!activeCompanyId) return;
      try {
        const { data } = await axios.get(
          `${API_BASE_URL}/api/service-providers`,
          { params: { company_id: activeCompanyId } },
        );
        // normalize ids to strings
        const normalized = (data.providers || []).map((p) => ({
          ...p,
          id: String(p.id),
        }));
        setProviders(normalized);
      } catch (err) {
        console.error("Error fetching providers:", err);
        setProviders([]);
      }
    };
    fetchProviders();
  }, [activeCompanyId]);

  // --- Normalize incoming value (supports both legacy and new shapes) ---
  // Legacy: value === [] or ['123']
  // New:    value === { selection: ['123'], tags: ['notify_audit_was_done'] }
  const selectionArray = useMemo(() => {
    // Legacy: plain array
    if (Array.isArray(value)) return value.map(String);
    // New: { selection: [...] }
    if (value && Array.isArray(value.selection))
      return value.selection.map(String);
    // Older legacy: { selectedIds: [...] }
    if (value && Array.isArray(value.selectedIds))
      return value.selectedIds.map(String);
    // Super-legacy: scalar id
    if (typeof value === "string" || typeof value === "number")
      return [String(value)];
    return [];
  }, [value]);

  const selectedId = selectionArray.length ? selectionArray[0] : "";

  const notifyChecked = useMemo(() => {
    if (Array.isArray(value)) return false; // legacy had no tags
    if (value && Array.isArray(value.tags)) {
      return value.tags.includes(TAG_NOTIFY);
    }
    return false;
  }, [value]);

  // --- Emit helper (always emits the new object shape) ---
  const emit = (nextSelectionArr, notify) => {
    const payload = { selection: nextSelectionArr };
    if (notify) payload.tags = [TAG_NOTIFY];
    onChange(payload);
  };

  const handleSelectChange = (e) => {
    const nextId = e.target.value ? String(e.target.value) : "";
    const nextSelection = nextId ? [nextId] : [];
    emit(nextSelection, notifyChecked);
  };

  const handleNotifyToggle = (e) => {
    const nextNotify = !!e.target.checked;
    emit(selectionArray, nextNotify);
  };

  const labelText = module.label || "Select Service Provider";
  const labelId = `sp-label-${module.id}`;
  const selectId = `sp-select-${module.id}`;
  const isError =
    Boolean(module.required) && (!selectedId || selectedId.length === 0);

  return (
    <Paper elevation={3} sx={{ p: 2, my: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Typography variant="subtitle1">{labelText}</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={notifyChecked}
              onChange={handleNotifyToggle}
              disabled={true}
            />
          }
          label="notify audit was done"
          sx={{ ml: 2, whiteSpace: "nowrap" }}
        />
      </Stack>

      <Box mt={1}>
        <FormControl fullWidth variant="outlined" error={isError} size="medium">
          <InputLabel id={labelId}>{labelText}</InputLabel>
          <Select
            labelId={labelId}
            id={selectId}
            label={labelText}
            value={selectedId}
            onChange={handleSelectChange}
            disabled={!isEditable}
          >
            {providers.length > 0 ? (
              providers.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                {activeCompanyId
                  ? "Loading providers..."
                  : "Company ID not set"}
              </MenuItem>
            )}
          </Select>
          {isError && (
            <FormHelperText>Please select a service provider</FormHelperText>
          )}
        </FormControl>
      </Box>
    </Paper>
  );
};

export default ServiceProviderSelectResponse;

/*
Notes:
- We write the tag as "notify_audit_was_done" (slugified) to avoid spaces in backend keys.
- Emission is always the object shape { selection: [...], tags?: [...] } for clarity and future-proofing.
- Legacy values (plain arrays) are still read correctly.
*/
